import { AnalyticsRouter } from '@monorepo/analytics/src/AnalyticsRouter';
import { DocumentsDataSetupRouter } from '@monorepo/documentsDataSetup/src/DocumentsDataSetupRouter';
import { FormsDataSetupRouter } from '@monorepo/formTemplates/src/FormsDataSetupRouter';
import { FormTemplateWizardRouter } from '@monorepo/formTemplates/src/FormTemplateWizardRouter';
import { InspectionsRouter } from '@monorepo/inspections/src/InspectionsRouter';
import { LogDataSetupRouter } from '@monorepo/logDataSetup/src/LogDataSetupRouter';
import { LogsRouter } from '@monorepo/logs/src/LogsRouter';
import { RecordsRouter } from '@monorepo/records/src/RecordsRouter';
import { ResourceDataSetupRouter } from '@monorepo/resourceDataSetup/src/ResourceDataSetupRouter';
import { DefaultDocumentTitle } from '@monorepo/shared/componentsV2/DocumentTitle';
import { RoutesWith404 } from '@monorepo/shared/componentsV2/RoutesWith404';
import { useHasAnalyticsAccess } from '@monorepo/shared/hooks/permissions/useHasAnalyticsPermissions';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { SiteDetailsRouter } from '@monorepo/siteDetails/src/SiteDetailsRouter';
import { SiteDocumentsRouter } from '@monorepo/siteDocuments/src/SiteDocumentsRouter';
import { ComplianceCalendarRouter } from '@monorepo/complianceCalendar/src/ComplianceCalendarRouter';
import { UsersRouter } from '@monorepo/users/src/UsersRouter';
import { WorkflowDataSetupRouter } from '@monorepo/workflowDataSetup/src/WorkflowDataSetupRouter';
import { useFeatureFlags } from 'mapistry-shared/api';
import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { BackboneAppUrlsToRedirectToVortexUrls } from './components/BackboneAppUrlsToRedirectToVortexUrls';
import { DataSetup } from './components/DataSetup';
import { Organization } from './components/Organization';
import { Project } from './components/Project';
import { ProjectLayout } from './components/ProjectLayout';

// TODO: Transition to a data router setup
// https://reactrouter.com/6.30.1/routers/picking-a-router#using-v64-data-apis
export function AppRouter() {
  const { search } = useLocation();
  const { isData2Enabled } = useFeatureFlags();
  const hasAnalyticsAccess = useHasAnalyticsAccess();
  const isSuperAdmin = useIsSuperAdmin();

  return (
    <>
      <DefaultDocumentTitle />
      <RoutesWith404>
        <Route path="/" element={<Navigate replace to="vortex" />} />
        <Route path="vortex">
          <Route index element={<Navigate replace to="organizations" />} />
          <Route path="organizations">
            <Route index element={<Organization />} />
            <Route path=":organizationId">
              <Route index element={<Navigate replace to="sites" />} />
              <Route path="sites">
                <Route index element={<Project />} />
                <Route path=":projectId" element={<ProjectLayout />}>
                  <Route index element={<Navigate replace to="calendar" />} />
                  <Route
                    path="tasks/*"
                    element={<Navigate replace to={`../calendar${search}`} />}
                  />
                  <Route
                    path="calendar/*"
                    element={<ComplianceCalendarRouter />}
                  />
                  <Route path="inspections/*" element={<InspectionsRouter />} />
                  <Route path="logs/*" element={<LogsRouter />} />
                  <Route path="records/*" element={<RecordsRouter />} />
                  <Route
                    path="site-details/*"
                    element={<SiteDetailsRouter />}
                  />
                  <Route path="documents/*" element={<SiteDocumentsRouter />} />
                  <Route path="users/*" element={<UsersRouter />} />
                </Route>
              </Route>
              <Route path="data-setup" element={<DataSetup />}>
                <Route
                  index
                  element={
                    <Navigate replace to={isData2Enabled ? 'logs' : 'forms'} />
                  }
                />
                {isData2Enabled && (
                  <>
                    <Route path="logs/*" element={<LogDataSetupRouter />} />
                    <Route
                      path="resource-types/*"
                      element={<ResourceDataSetupRouter />}
                    />
                    <Route
                      path="workflows/*"
                      element={<WorkflowDataSetupRouter />}
                    />
                  </>
                )}
                <Route path="forms/*" element={<FormsDataSetupRouter />} />
                <Route
                  path="documents/*"
                  element={<DocumentsDataSetupRouter />}
                />
              </Route>
            </Route>
          </Route>
          {hasAnalyticsAccess && (
            <Route path="analytics/*" element={<AnalyticsRouter />} />
          )}
          {isSuperAdmin && (
            <Route
              path="admin/form-templates/new/*"
              element={
                <FormTemplateWizardRouter
                  isUsingOrgSpecificEditor={false}
                  hasOrgUpdatePermissions={false} // we're outside of any specific org here
                  isEditMode={false}
                  formWizardBasePath="/vortex/admin/form-templates"
                />
              }
            />
          )}
        </Route>
        {BackboneAppUrlsToRedirectToVortexUrls()}
      </RoutesWith404>
    </>
  );
}
